import React, { useEffect, useRef, useState } from "react";
import { Link, useHistory } from "react-router-dom";
import axios from "axios";
import swal from "sweetalert";
import { useTranslation } from "react-i18next";

const KEY = "eyJ0eXAiOiJKV1Qidfg%!#%$%LCdf#*&*JhbGciOiJSUzI1NiJ9";
const EMPTY = { w_select: "", subject: "", message: "" };

const Icon = ({ name, size = 20 }) => {
  const common = { width: size, height: size, viewBox: "0 0 24 24", fill: "none", stroke: "currentColor", strokeWidth: 1.8, strokeLinecap: "round", strokeLinejoin: "round", 'aria-hidden': true };
  const paths = {
    ticket: <><path d="M4 6h16v4a2 2 0 0 0 0 4v4H4v-4a2 2 0 0 0 0-4V6Z"/><path d="M12 6v2m0 3v2m0 3v2"/></>,
    arrow: <><path d="M5 12h14m-6-6 6 6-6 6"/></>,
    shield: <><path d="m12 2 8 4v6c0 5-3.5 8-8 10-4.5-2-8-5-8-10V6l8-4Z"/><path d="m9 12 2 2 4-4"/></>,
    send: <><path d="m22 2-7 20-4-9-9-4 20-7Z"/><path d="M22 2 11 13"/></>,
    list: <><path d="M8 6h13M8 12h13M8 18h13"/><path d="M3 6h.01M3 12h.01M3 18h.01"/></>,
    info: <><circle cx="12" cy="12" r="10"/><path d="M12 11v6m0-10h.01"/></>,
    check: <><circle cx="12" cy="12" r="10"/><path d="m8 12 3 3 5-6"/></>,
  };
  return <svg {...common}>{paths[name] || paths.info}</svg>;
};

export default function Checkout() {
  const history = useHistory();
  const { t } = useTranslation();
  const [options, setOptions] = useState([]);
  const [form, setForm] = useState(EMPTY);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const submittingRef = useRef(false);

  useEffect(() => {
    let active = true;
    const controller = new AbortController();
    axios.post("/new-ticket", { key_s: KEY }, { signal: controller.signal })
      .then(({ data }) => { if (active) setOptions(Array.isArray(data?.data) ? data.data : []); })
      .catch((error) => { if (active && error?.code !== "ERR_CANCELED") console.error("Ticket types unavailable", error); });
    return () => { active = false; controller.abort(); };
  }, []);

  const change = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    setErrors((prev) => ({ ...prev, [name]: undefined }));
  };

  const submit = async (event) => {
    event.preventDefault();
    if (submittingRef.current) return;
    if (!localStorage.getItem("auth_token")) {
      swal("Session expired", "Please sign in before submitting a ticket.", "warning");
      return;
    }
    const nextErrors = {};
    if (!form.w_select) nextErrors.w_select = "Please select a ticket type.";
    if (!form.subject.trim()) nextErrors.subject = "Please enter a subject.";
    if (!form.message.trim()) nextErrors.message = "Please describe your issue.";
    if (Object.keys(nextErrors).length) { setErrors(nextErrors); return; }

    submittingRef.current = true;
    setSubmitting(true);
    try {
      const { data } = await axios.post("/new-ticket-post", {
        subject: form.subject.trim(), message: form.message.trim(),
        w_select: form.w_select, key_s: KEY,
      });
      if (data?.status === "success") {
        setForm(EMPTY);
        setErrors({});
        await swal("Success", data.message || "Your ticket has been submitted.", "success");
        history.push("/support-ticket");
      } else if (data?.status === "fail") {
        swal("Warning", data.message || "Unable to submit ticket.", "warning");
      } else {
        setErrors(data?.validation_error || {});
        if (!data?.validation_error) swal("Warning", data?.message || "Please check the form.", "warning");
      }
    } catch (error) {
      if (error?.response?.data?.validation_error) setErrors(error.response.data.validation_error);
      swal("Error", error?.response?.data?.message || "Unable to connect. Please try again.", "error");
    } finally {
      submittingRef.current = false;
      setSubmitting(false);
    }
  };

  const errorText = (field) => Array.isArray(errors[field]) ? errors[field].join(" ") : (errors[field] || "");
  return (
    <>
      <style>{`
        html,body,#root{background:#00191D!important}
        .rmt-root,.rmt-root *{box-sizing:border-box}
        .rmt-root{--navy:#00191D;--cyan:#16B8C7;--muted:#9bb3b8;--line:#204249;background:#00191D;color:#fff;min-height:100vh;padding:20px 4px 44px;font-size:14px;font-family:inherit}
        .rmt-wrap{max-width:1160px;margin:auto;width:100%}
        .rmt-top{display:flex;align-items:center;justify-content:space-between;gap:16px;margin-bottom:18px}
        .rmt-eyebrow{color:#68d9e2;font-size:11px;font-weight:800;letter-spacing:1.7px;text-transform:uppercase;margin-bottom:7px}
        .rmt-title{font-size:clamp(23px,3vw,32px);font-weight:800;letter-spacing:-.8px;margin:0 0 6px;color:white}
        .rmt-desc{color:#a8bdc2;margin:0;line-height:1.6}
        .rmt-link{display:inline-flex;align-items:center;justify-content:center;gap:8px;white-space:nowrap;padding:11px 15px;border:1px solid #315b62;border-radius:12px;background:#08282e;color:white!important;text-decoration:none!important;font-weight:700;transition:background .2s,transform .2s}
        .rmt-link:hover{background:#103b43;transform:translateY(-2px)}
        .rmt-layout{display:grid;grid-template-columns:minmax(0,1.7fr) minmax(270px,.8fr);gap:16px;align-items:start}
        .rmt-card{background:#06252b;border:1px solid #21444b;border-radius:20px;overflow:hidden;animation:rmt-in .35s ease both}
        .rmt-card-head{padding:22px 24px;border-bottom:1px solid #21444b;display:flex;gap:13px;align-items:center}
        .rmt-head-icon{height:44px;width:44px;display:grid;place-items:center;border-radius:13px;background:linear-gradient(135deg,#16B8C7,#7ee9ef);color:#003039;flex:none}
        .rmt-card-head h2{font-size:18px;font-weight:800;margin:0 0 4px;color:#fff}
        .rmt-card-head p{color:#a5bdc1;font-size:13px;margin:0}
        .rmt-form{padding:24px}
        .rmt-field{margin-bottom:20px}
        .rmt-label{display:block;color:#eaf6f8;font-size:14px;font-weight:700;margin:0 0 9px}
        .rmt-required{color:#60dce6}
        .rmt-control{display:block;width:100%;height:48px;border:1px solid #31545c;background:#001b20;color:white;border-radius:11px;padding:0 14px;outline:none;font-size:14px;transition:border-color .2s,box-shadow .2s}
        .rmt-control::placeholder{color:#829da4}
        .rmt-control:focus{border-color:#16B8C7;box-shadow:0 0 0 3px rgba(22,184,199,.13)}
        .rmt-control option{background:#06252b;color:white}
        textarea.rmt-control{height:auto;min-height:175px;resize:vertical;padding:14px;line-height:1.6}
        .rmt-error{color:#ff9b9b;font-size:12px;margin:6px 0 0;display:block}
        .rmt-form-foot{display:flex;align-items:center;justify-content:space-between;gap:12px;border-top:1px solid #24464c;padding-top:20px}
        .rmt-hint{font-size:12px;color:#a5bdc1}
        .rmt-submit{border:0;border-radius:11px;min-height:46px;padding:0 20px;background:linear-gradient(110deg,#16B8C7,#65e2e9);color:#002b33;font-size:14px;font-weight:800;display:inline-flex;align-items:center;justify-content:center;gap:9px;cursor:pointer;transition:transform .2s,filter .2s}
        .rmt-submit:hover:not(:disabled){transform:translateY(-2px);filter:brightness(1.06)}
        .rmt-submit:disabled{opacity:.65;cursor:not-allowed}
        .rmt-aside{display:grid;gap:16px}
        .rmt-white{background:#fff;color:#102d33;border-radius:20px;padding:23px;border:1px solid #d9ebed;animation:rmt-in .4s ease both}
        .rmt-white-icon{width:42px;height:42px;border-radius:13px;background:#e0f7f9;color:#087f8a;display:grid;place-items:center;margin-bottom:16px}
        .rmt-white h3{color:#092c33;font-size:17px;font-weight:800;margin:0 0 10px}
        .rmt-white p{color:#546b70;line-height:1.7;font-size:13px;margin:0}
        .rmt-step{display:flex;gap:12px;padding:13px 0;border-bottom:1px solid #e8eff0}
        .rmt-step:last-child{border-bottom:0;padding-bottom:0}
        .rmt-step-number{width:26px;height:26px;border-radius:9px;display:grid;place-items:center;background:#e0f7f9;color:#007f8d;font-weight:800;flex:none;font-size:12px}
        .rmt-step strong{display:block;font-size:13px;color:#18383d;margin-bottom:3px}
        .rmt-step span{font-size:12px;color:#627b80;line-height:1.5}
        .rmt-note{padding:20px;border-radius:18px;border:1px solid #245057;background:#082b31;display:flex;gap:12px;color:#c1d6d9;line-height:1.6;font-size:13px}
        .rmt-note svg{color:#4bd0dc;flex:none}
        @keyframes rmt-in{from{opacity:.6;transform:translateY(8px)}to{opacity:1;transform:translateY(0)}}
        @media(max-width:850px){.rmt-layout{grid-template-columns:1fr}.rmt-aside{grid-template-columns:1fr 1fr}}
        @media(max-width:600px){.rmt-root{padding-top:14px}.rmt-top{align-items:flex-start;flex-direction:column}.rmt-link{width:100%}.rmt-card-head{padding:17px}.rmt-form{padding:17px}.rmt-aside{grid-template-columns:1fr}.rmt-form-foot{align-items:stretch;flex-direction:column}.rmt-submit{width:100%}.rmt-white{padding:18px}}
        @media(prefers-reduced-motion:reduce){.rmt-root *{animation:none!important;transition:none!important}}
      `}</style>
      <main className="rmt-root">
        <div className="rmt-wrap">
          <header className="rmt-top">
            <div>
              <div className="rmt-eyebrow">RM CROWN MARKET / Help Center</div>
              <h1 className="rmt-title">Create Support Ticket</h1>
              <p className="rmt-desc">Tell us what you need help with. Our support team will review your request.</p>
            </div>
            <Link to="/support-ticket" className="rmt-link"><Icon name="list" size={17}/> All Tickets <Icon name="arrow" size={16}/></Link>
          </header>
          <div className="rmt-layout">
            <section className="rmt-card" aria-labelledby="rmt-form-title">
              <div className="rmt-card-head">
                <div className="rmt-head-icon"><Icon name="ticket" size={23}/></div>
                <div><h2 id="rmt-form-title">New Support Request</h2><p>Complete the details below to submit a ticket</p></div>
              </div>
              <form className="rmt-form" onSubmit={submit}>
                <div className="rmt-field">
                  <label className="rmt-label" htmlFor="rmt-type">Ticket Type <span className="rmt-required">*</span></label>
                  <select id="rmt-type" name="w_select" value={form.w_select} onChange={change} className="rmt-control" required aria-invalid={Boolean(errorText("w_select"))}>
                    <option value="">{t("pro_with_sel_get")}</option>
                    {options.map((item, index) => <option key={`${item.value}-${index}`} value={item.value}>{item.label}</option>)}
                  </select>
                  {errorText("w_select") && <span className="rmt-error" role="alert">{errorText("w_select")}</span>}
                </div>
                <div className="rmt-field">
                  <label className="rmt-label" htmlFor="rmt-subject">Subject <span className="rmt-required">*</span></label>
                  <input id="rmt-subject" className="rmt-control" type="text" name="subject" value={form.subject} onChange={change} placeholder="Briefly describe your issue" required aria-invalid={Boolean(errorText("subject"))}/>
                  {errorText("subject") && <span className="rmt-error" role="alert">{errorText("subject")}</span>}
                </div>
                <div className="rmt-field">
                  <label className="rmt-label" htmlFor="rmt-message">Message <span className="rmt-required">*</span></label>
                  <textarea id="rmt-message" className="rmt-control" name="message" value={form.message} onChange={change} rows={8} placeholder="Describe the problem, what you expected, and any relevant details..." required aria-invalid={Boolean(errorText("message"))}/>
                  {errorText("message") && <span className="rmt-error" role="alert">{errorText("message")}</span>}
                </div>
                <div className="rmt-form-foot">
                  <span className="rmt-hint">* Required fields</span>
                  <button type="submit" className="rmt-submit" disabled={submitting}><Icon name="send" size={17}/>{submitting ? "Submitting..." : "Submit Ticket"}</button>
                </div>
              </form>
            </section>
            <aside className="rmt-aside">
              <section className="rmt-white">
                <div className="rmt-white-icon"><Icon name="check" size={23}/></div>
                <h3>Get the right support</h3>
                <p>A clear subject and detailed message help the support team understand your request.</p>
                <div className="rmt-step"><div className="rmt-step-number">01</div><div><strong>Choose a category</strong><span>Select the ticket type that best matches your issue.</span></div></div>
                <div className="rmt-step"><div className="rmt-step-number">02</div><div><strong>Explain the problem</strong><span>Include the relevant details in your message.</span></div></div>
                <div className="rmt-step"><div className="rmt-step-number">03</div><div><strong>Track your request</strong><span>View your submitted ticket from All Tickets.</span></div></div>
              </section>
              <div className="rmt-note"><Icon name="shield" size={21}/><span>For your security, never include account passwords, OTP codes, or private keys in a support ticket.</span></div>
            </aside>
          </div>
        </div>
      </main>
    </>
  );
}
